import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Trigger, 
  TriggerCategory, 
  SensorTriggerConfig, 
  ForecastTriggerConfig 
} from '@/types';
import { 
  Zap, 
  Droplets, 
  Thermometer, 
  CloudRain, 
  Sun,
  StopCircle,
  Play,
  Timer,
  ArrowDown,
  ArrowUp,
  MapPin
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ApiCompany } from '@/types/api';
import type { User } from '@/types';
import { TriggerZoneSelector, type GardenOption } from './TriggerZoneSelector';
import { gardensService } from '@/services';

interface NativeSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  options: { value: string; label: string }[];
  className?: string;
  disabled?: boolean;
}

function NativeSelect({ value, onChange, placeholder, options, className, disabled }: NativeSelectProps) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(
          'w-full h-10 px-3 rounded-md border border-input bg-background text-sm ring-offset-background',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface TriggerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: Trigger | null;
  /** Al guardar, se pasa el trigger y opcionalmente los zoneIds vinculados (v0.3) */
  onSave: (trigger: Trigger, linkedZoneIds?: string[]) => void;
  isLoading?: boolean;
  companies?: ApiCompany[];
  currentUser?: User | null;
  isSuperAdmin?: boolean;
  /** Jardines con zonas para el selector de vinculación (v0.3) */
  gardens?: GardenOption[];
  /** Zonas ya vinculadas al trigger (al editar); se cargan con GET link-zones */
  initialLinkedZoneIds?: string[];
}

export function TriggerFormDialog({
  open,
  onOpenChange,
  trigger,
  onSave,
  isLoading = false,
  companies = [],
  currentUser,
  isSuperAdmin = false,
  gardens = [],
  initialLinkedZoneIds = [],
}: TriggerFormDialogProps) {
  const isEditing = !!trigger;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [category, setCategory] = useState<TriggerCategory>('sensor');
  const [companyId, setCompanyId] = useState<string>(''); // Para superadmin
  const [selectedZoneIds, setSelectedZoneIds] = useState<string[]>([]);

  /** Empresa efectiva: la del trigger o la seleccionada (superadmin) o la del usuario */
  const effectiveCompanyId = isSuperAdmin ? companyId : currentUser?.companyId;

  /** Jardines con zonas de la empresa seleccionada; se cargan al elegir empresa y se actualizan al cambiar */
  const { data: gardensWithZones = [], isLoading: isLoadingGardensZones } = useQuery({
    queryKey: ['gardens-with-zones', effectiveCompanyId],
    queryFn: async (): Promise<GardenOption[]> => {
      if (!effectiveCompanyId) return [];
      const gardensRes = await gardensService.getAll({ companyId: effectiveCompanyId, pageSize: 100 });
      const list = gardensRes.success && gardensRes.data?.items ? gardensRes.data.items : [];
      const withZones = await Promise.all(
        list.map(async (g) => {
          const zonesRes = await gardensService.getZones(g.id);
          const zones = zonesRes.success && zonesRes.data?.items ? zonesRes.data.items : [];
          return {
            id: g.id,
            name: g.name,
            companyId: g.companyId,
            zones: zones.map((z) => ({ id: z.id, name: z.name, gardenId: g.id })),
          };
        })
      );
      return withZones;
    },
    enabled: !!effectiveCompanyId && open,
  });
  
  // Sensor config
  const [sensorType, setSensorType] = useState<'humidity' | 'temperature'>('humidity');
  const [sensorCondition, setSensorCondition] = useState<'gt' | 'lt'>('lt');
  const [sensorThreshold, setSensorThreshold] = useState<number>(30);
  const [sensorAction, setSensorAction] = useState<'stop_irrigation' | 'start_irrigation'>('start_irrigation');
  const [sensorDuration, setSensorDuration] = useState<number>(15);
  
  // Forecast config
  const [forecastType, setForecastType] = useState<'rain' | 'temperature'>('rain');
  const [forecastCondition, setForecastCondition] = useState<'rain_probability_gt' | 'temperature_gt' | 'temperature_lt'>('rain_probability_gt');
  const [forecastThreshold, setForecastThreshold] = useState<number>(80);
  const [forecastAction, setForecastAction] = useState<'cancel_irrigation' | 'add_irrigation'>('cancel_irrigation');
  const [forecastDuration, setForecastDuration] = useState<number>(20);

  useEffect(() => {
    if (trigger) {
      setName(trigger.name);
      setDescription(trigger.description || '');
      setEnabled(trigger.enabled);
      setCategory(trigger.category);
      setCompanyId(trigger.companyId || ''); // Inicializar companyId si está editando
      
      if (trigger.sensorConfig) {
        setSensorType(trigger.sensorConfig.sensorType);
        setSensorCondition(trigger.sensorConfig.condition);
        setSensorThreshold(trigger.sensorConfig.threshold);
        setSensorAction(trigger.sensorConfig.action);
        setSensorDuration(trigger.sensorConfig.irrigationDuration || 15);
      }
      
      if (trigger.forecastConfig) {
        setForecastType(trigger.forecastConfig.forecastType);
        setForecastCondition(trigger.forecastConfig.condition);
        setForecastThreshold(trigger.forecastConfig.threshold || 35);
        setForecastAction(trigger.forecastConfig.action);
        setForecastDuration(trigger.forecastConfig.irrigationDuration || 20);
      }
    } else {
      // Reset form
      setName('');
      setDescription('');
      setEnabled(true);
      setCategory('sensor');
      setCompanyId(''); // Reset companyId
      setSensorType('humidity');
      setSensorCondition('lt');
      setSensorThreshold(30);
      setSensorAction('start_irrigation');
      setSensorDuration(15);
      setForecastType('rain');
      setForecastCondition('rain_probability_gt');
      setForecastThreshold(80);
      setForecastAction('cancel_irrigation');
      setForecastDuration(20);
    }
    if (open) {
      setSelectedZoneIds(trigger ? initialLinkedZoneIds : []);
    }
  }, [trigger, open, initialLinkedZoneIds]);

  const handleSubmit = () => {
    const now = new Date();
    
    const sensorConfig: SensorTriggerConfig | undefined = category === 'sensor' ? {
      sensorType,
      condition: sensorCondition,
      threshold: sensorThreshold,
      action: sensorAction,
      irrigationDuration: sensorAction === 'start_irrigation' ? sensorDuration : undefined,
    } : undefined;
    
    const forecastConfig: ForecastTriggerConfig | undefined = category === 'forecast' ? {
      forecastType,
      condition: forecastCondition,
      threshold: forecastThreshold,
      action: forecastAction,
      irrigationDuration: forecastAction === 'add_irrigation' ? forecastDuration : undefined,
    } : undefined;

    const newTrigger: Trigger = {
      id: trigger?.id || `trigger-${Date.now()}`,
      companyId: isSuperAdmin && companyId ? companyId : trigger?.companyId, // Incluir companyId si es superadmin y se seleccionó
      name,
      description: description || undefined,
      category,
      sensorConfig,
      forecastConfig,
      enabled,
      zoneAssignments: trigger?.zoneAssignments || [],
      createdAt: trigger?.createdAt || now,
      updatedAt: now,
    };

    onSave(newTrigger, selectedZoneIds);
    onOpenChange(false);
  };

  // Option button component for radio-like behavior
  const OptionButton = ({ 
    selected, 
    onClick, 
    icon: Icon, 
    label, 
    variant = 'default' 
  }: { 
    selected: boolean; 
    onClick: () => void; 
    icon?: React.ElementType; 
    label: string;
    variant?: 'default' | 'success' | 'destructive';
  }) => {
    const variantStyles = {
      default: selected ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50',
      success: selected ? 'border-green-500 bg-green-500/10 text-green-600' : 'border-border hover:border-green-500/50',
      destructive: selected ? 'border-destructive bg-destructive/10 text-destructive' : 'border-border hover:border-destructive/50',
    };

    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-all",
          variantStyles[variant]
        )}
      >
        {Icon && <Icon className="w-4 h-4" />}
        {label}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            {isEditing ? 'Editar Trigger' : 'Nuevo Trigger'}
          </DialogTitle>
          <DialogDescription>
            Configura las condiciones y acciones del trigger
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Basic info */}
          {/* Empresa (solo para superadmin) */}
          {isSuperAdmin && (
            <div className="space-y-2">
              <Label htmlFor="company">Empresa</Label>
              <NativeSelect
                value={companyId}
                onChange={(value) => {
                  setCompanyId(value);
                  setSelectedZoneIds([]);
                }}
                placeholder="Seleccionar empresa (opcional para trigger global)"
                options={[
                  { value: '', label: 'Sin empresa (trigger global)' },
                  ...companies.map(c => ({ value: c.id, label: c.name }))
                ]}
              />
              <p className="text-xs text-muted-foreground">
                Selecciona una empresa o déjalo vacío para crear un trigger global
              </p>
            </div>
          )}
          
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Riego por humedad baja"
              />
            </div>
            <div className="flex items-center justify-between sm:justify-end gap-4 pt-6">
              <Label htmlFor="enabled">Activo</Label>
              <Switch
                id="enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción opcional del trigger..."
              rows={2}
            />
          </div>

          {/* Category tabs */}
          <Tabs value={category} onValueChange={(v) => setCategory(v as TriggerCategory)}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="sensor" className="gap-2">
                <Droplets className="w-4 h-4" />
                Por Sensores
              </TabsTrigger>
              <TabsTrigger value="forecast" className="gap-2">
                <CloudRain className="w-4 h-4" />
                Por Previsión
              </TabsTrigger>
            </TabsList>

            {/* Sensor-based trigger */}
            <TabsContent value="sensor" className="space-y-4 mt-4">
              <Card>
                <CardContent className="pt-4 space-y-4">
                  {/* Tipo de sensor */}
                  <div className="space-y-2">
                    <Label>Tipo de sensor</Label>
                    <div className="flex flex-wrap gap-2">
                      <OptionButton
                        selected={sensorType === 'humidity'}
                        onClick={() => setSensorType('humidity')}
                        icon={Droplets}
                        label="Humedad"
                      />
                      <OptionButton
                        selected={sensorType === 'temperature'}
                        onClick={() => setSensorType('temperature')}
                        icon={Thermometer}
                        label="Temperatura"
                      />
                    </div>
                  </div>

                  {/* Condición */}
                  <div className="space-y-2">
                    <Label>Condición</Label>
                    <div className="flex flex-wrap gap-2">
                      <OptionButton
                        selected={sensorCondition === 'lt'}
                        onClick={() => setSensorCondition('lt')}
                        icon={ArrowDown}
                        label="Por debajo de"
                      />
                      <OptionButton
                        selected={sensorCondition === 'gt'}
                        onClick={() => setSensorCondition('gt')}
                        icon={ArrowUp}
                        label="Por encima de"
                      />
                    </div>
                  </div>

                  {/* Umbral */}
                  <div className="space-y-2">
                    <Label>Umbral</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={sensorThreshold}
                        onChange={(e) => setSensorThreshold(Number(e.target.value))}
                        className="w-24"
                      />
                      <span className="text-muted-foreground">
                        {sensorType === 'humidity' ? '%' : '°C'}
                      </span>
                    </div>
                  </div>

                  {/* Acción */}
                  <div className="space-y-2">
                    <Label>Acción</Label>
                    <div className="flex flex-wrap gap-2">
                      <OptionButton
                        selected={sensorAction === 'start_irrigation'}
                        onClick={() => setSensorAction('start_irrigation')}
                        icon={Play}
                        label="Iniciar riego"
                        variant="success"
                      />
                      <OptionButton
                        selected={sensorAction === 'stop_irrigation'}
                        onClick={() => setSensorAction('stop_irrigation')}
                        icon={StopCircle}
                        label="Detener riego"
                        variant="destructive"
                      />
                    </div>
                  </div>

                  {sensorAction === 'start_irrigation' && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Timer className="w-4 h-4" />
                        Duración del riego
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={sensorDuration}
                          onChange={(e) => setSensorDuration(Number(e.target.value))}
                          min={1}
                          max={120}
                          className="w-24"
                        />
                        <span className="text-muted-foreground">minutos</span>
                      </div>
                    </div>
                  )}

                  {/* Preview */}
                  <div className="p-3 rounded-lg bg-muted/50 text-sm">
                    <span className="text-muted-foreground">Resumen: </span>
                    Cuando la <strong>{sensorType === 'humidity' ? 'humedad' : 'temperatura'}</strong> esté{' '}
                    <strong>{sensorCondition === 'lt' ? 'por debajo de' : 'por encima de'}</strong>{' '}
                    <strong>{sensorThreshold}{sensorType === 'humidity' ? '%' : '°C'}</strong>,{' '}
                    <strong>{sensorAction === 'stop_irrigation' ? 'detener el riego' : `iniciar riego durante ${sensorDuration} min`}</strong>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Forecast-based trigger */}
            <TabsContent value="forecast" className="space-y-4 mt-4">
              <Card>
                <CardContent className="pt-4 space-y-4">
                  {/* Tipo de previsión */}
                  <div className="space-y-2">
                    <Label>Tipo de previsión</Label>
                    <div className="flex flex-wrap gap-2">
                      <OptionButton
                        selected={forecastType === 'rain'}
                        onClick={() => {
                          setForecastType('rain');
                          setForecastCondition('rain_probability_gt');
                          setForecastThreshold(80);
                          setForecastAction('cancel_irrigation');
                        }}
                        icon={CloudRain}
                        label="Probabilidad de lluvia"
                      />
                      <OptionButton
                        selected={forecastType === 'temperature'}
                        onClick={() => {
                          setForecastType('temperature');
                          setForecastCondition('temperature_gt');
                          setForecastThreshold(35);
                          setForecastAction('add_irrigation');
                        }}
                        icon={Sun}
                        label="Temperatura"
                      />
                    </div>
                  </div>

                  {/* Condición para temperatura */}
                  {forecastType === 'temperature' && (
                    <div className="space-y-2">
                      <Label>Condición</Label>
                      <div className="flex flex-wrap gap-2">
                        <OptionButton
                          selected={forecastCondition === 'temperature_gt'}
                          onClick={() => setForecastCondition('temperature_gt')}
                          icon={ArrowUp}
                          label="Superior a"
                        />
                        <OptionButton
                          selected={forecastCondition === 'temperature_lt'}
                          onClick={() => setForecastCondition('temperature_lt')}
                          icon={ArrowDown}
                          label="Inferior a"
                        />
                      </div>
                    </div>
                  )}

                  {/* Umbral */}
                  <div className="space-y-2">
                    <Label>
                      {forecastType === 'rain' ? 'Probabilidad de lluvia superior a' : 'Temperatura'}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={forecastThreshold}
                        onChange={(e) => setForecastThreshold(Number(e.target.value))}
                        min={forecastType === 'rain' ? 0 : -20}
                        max={forecastType === 'rain' ? 100 : 50}
                        className="w-24"
                      />
                      <span className="text-muted-foreground">
                        {forecastType === 'rain' ? '%' : '°C'}
                      </span>
                    </div>
                  </div>

                  {/* Acción */}
                  <div className="space-y-2">
                    <Label>Acción</Label>
                    <div className="flex flex-wrap gap-2">
                      <OptionButton
                        selected={forecastAction === 'add_irrigation'}
                        onClick={() => setForecastAction('add_irrigation')}
                        icon={Play}
                        label="Añadir riego"
                        variant="success"
                      />
                      <OptionButton
                        selected={forecastAction === 'cancel_irrigation'}
                        onClick={() => setForecastAction('cancel_irrigation')}
                        icon={StopCircle}
                        label="Anular riego"
                        variant="destructive"
                      />
                    </div>
                  </div>

                  {forecastAction === 'add_irrigation' && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Timer className="w-4 h-4" />
                        Duración del riego
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={forecastDuration}
                          onChange={(e) => setForecastDuration(Number(e.target.value))}
                          min={1}
                          max={120}
                          className="w-24"
                        />
                        <span className="text-muted-foreground">minutos</span>
                      </div>
                    </div>
                  )}

                  {/* Preview */}
                  <div className="p-3 rounded-lg bg-muted/50 text-sm">
                    <span className="text-muted-foreground">Resumen: </span>
                    {forecastType === 'rain' ? (
                      <>
                        Cuando la <strong>probabilidad de lluvia</strong> sea{' '}
                        <strong>superior al {forecastThreshold}%</strong>,{' '}
                        <strong>{forecastAction === 'cancel_irrigation' ? 'anular el riego programado' : `añadir riego durante ${forecastDuration} min`}</strong>
                      </>
                    ) : (
                      <>
                        Cuando la <strong>temperatura</strong> sea{' '}
                        <strong>{forecastCondition === 'temperature_gt' ? 'superior' : 'inferior'} a {forecastThreshold}°C</strong>,{' '}
                        <strong>{forecastAction === 'cancel_irrigation' ? 'anular el riego programado' : `añadir riego durante ${forecastDuration} min`}</strong>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            </Tabs>

          {/* Vinculación a zonas (v0.3): se cargan tras seleccionar empresa y se actualizan al cambiar */}
          {(effectiveCompanyId || isEditing) && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Vinculación a zonas
              </Label>
              {!effectiveCompanyId && isEditing ? (
                <p className="text-xs text-muted-foreground">
                  Trigger global: no se puede restringir por zonas.
                </p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    Solo se muestran zonas de la empresa seleccionada. Sin zonas = trigger aplica a toda la empresa.
                  </p>
                  {isLoadingGardensZones ? (
                    <p className="text-sm text-muted-foreground py-4">Cargando jardines y zonas…</p>
                  ) : (
                    <TriggerZoneSelector
                      gardens={gardensWithZones}
                      selectedZoneIds={selectedZoneIds}
                      onChange={setSelectedZoneIds}
                      disabled={isLoading}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || isLoading}>
            {isLoading ? 'Guardando...' : isEditing ? 'Guardar Cambios' : 'Crear Trigger'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
